import React, { useEffect, useState } from 'react';
import Loading from './Loading';
import PageSizeSelector from './PageSizeSelector';
import Pager from './Pager';
import SearchInput from './SearchInput';

import axios from 'axios';
import ErrorMessage from '../ErrorMessage';
import httpClient from '../../../../infrastructure/http/httpClient';
import OrderByTypeSelector from '../../../projects/components/selectors/OrderByTypeSelector';
import OrderIsAscendingSelector from '../../../projects/components/selectors/OrderIsAscendingSelector';

interface FilteredQuery {
    totalRecordsCount: number;
    filteredRecordsCount: number;
    data: any[];
}

interface TableComponentWithDataProps {
    data: any[];
    requestReload?: () => void;
    onSortingChange?: (columnName: string, sortOrder: 'asc' | 'desc') => void;
    sortedBy?: string | null;
    ascending?: boolean | null;
}

export default function withFilteredRequestWithTypes(
    url: string,
    Component: React.ComponentType<TableComponentWithDataProps>
) {
    return function () {
        const [isLoading, setIsLoading] = useState<boolean>(false);
        const [data, setData] = useState<any>([]);
        const [searchTerm, setSearchTerm] = useState<string>('');
        const [pageIndex, setPageIndex] = useState<number>(0);
        const [pageSize, setPageSize] = useState<number>(10);
        const [filteredRecords, setFilteredRecords] = useState<number>(0);
        const [totalRecords, setTotalRecords] = useState<number>(0);
        const [hasErrors, setHasErrors] = useState<boolean>(false);
        const [errorDescription, setErrorDescription] = useState<string>('');
        const [dataReloadedCount, setDataReloadedCount] = useState(0);
        const [sortingColumn, setSortingColumn] = useState<string>('');
        const [ascending, setAscending] = useState<boolean>(true);

        function buildRequestUrl(url: string) {
            let requestUrl = `${process.env.REACT_APP_API_URL}/${url}`;

            if (requestUrl.indexOf('=') > -1) {
                requestUrl = `${requestUrl}&searchTerm=${searchTerm}&offset=${
                    pageIndex * pageSize
                }&top=${pageSize}&orderBy=${sortingColumn}&ascending=${ascending}`;
                return requestUrl;
            }

            requestUrl = `${requestUrl}?searchTerm=${searchTerm}&offset=${
                pageIndex * pageSize
            }&top=${pageSize}&orderBy=${sortingColumn}&ascending=${ascending}`;
            return requestUrl;
        }

        const handleSortingChange = (
            columnName: string,
            sortOrder: 'asc' | 'desc'
        ) => {
            setAscending(sortOrder === 'asc');
            setSortingColumn(columnName);
        };

        const requestReloadHandler = () => {
            setDataReloadedCount(dataReloadedCount + 1);
        };

        useEffect(() => {
            setIsLoading(true);
            setHasErrors(false);
            setErrorDescription('');
            const requestUrl: string = buildRequestUrl(url);
            httpClient
                .get(requestUrl)
                .then((response) => {
                    setData(response.data);
                    setFilteredRecords(response.filteredRecordsCount);
                    setTotalRecords(response.totalRecordsCount);
                    setIsLoading(false);
                })
                .catch((error) => {
                    setHasErrors(true);
                    setIsLoading(false);
                    setErrorDescription(error.message);
                });
        }, [
            searchTerm,
            pageIndex,
            pageSize,
            dataReloadedCount,
            sortingColumn,
            ascending,
        ]);

        function handleSearchTermChange(e: any) {
            setPageIndex(0);
            setSearchTerm(e.target.value);
        }

        function handlePageIndexChange(page: number) {
            setPageIndex(page);
        }

        function handlePageSizeChange(e: any) {
            setPageIndex(0);
            setPageSize(parseInt(e.target.value));
        }

        return (
            <>
                <div className='row' style={{ marginBottom: '0.5em' }}>
                    <div className='col-sm-6 col'>
                        <PageSizeSelector
                            initialPageSize={pageSize}
                            handleChange={handlePageSizeChange}
                        />
                    </div>
                    <div className='col-sm-6 col'>
                        <div className='d-flex justify-content-sm-end gap-2'>
                            <div>
                                <OrderIsAscendingSelector
                                    onChange={setAscending}
                                    value={ascending}
                                />
                            </div>
                            <div>
                                <OrderByTypeSelector
                                    onChange={setSortingColumn}
                                    value={sortingColumn}
                                />
                            </div>
                            <div className='search-box ms-2 gridjs-search'>
                                <SearchInput
                                    value={searchTerm}
                                    handleChange={handleSearchTermChange}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                {isLoading ? (
                    <Loading hasSkeleton={true} skeletonType='table' />
                ) : hasErrors ? (
                    <ErrorMessage errorDescription={errorDescription} />
                ) : (
                    <Component
                        sortedBy={sortingColumn}
                        ascending={ascending}
                        onSortingChange={handleSortingChange}
                        data={data}
                        requestReload={requestReloadHandler}
                    />
                )}
                <Pager
                    visible={!isLoading && !hasErrors}
                    handleChange={handlePageIndexChange}
                    currentPageIndex={pageIndex}
                    pageSize={pageSize}
                    filteredRecords={filteredRecords}
                    totalRecords={totalRecords}
                />
            </>
        );
    };
}
